/**
 * Admin Clients Page - displays all clients in a table format
 * Accessible only to super_admin users
 * @component AdminClientsPage
 * @file AdminClientsPage.tsx
 * @path frontend/src/pages/AdminClientsPage/AdminClientsPage.tsx
 */

import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import LoadingSpinner from '../../components/LoadingSpinner';
import Tooltip from '../../components/Tooltip';
import Popconfirm from '../../components/Popconfirm/Popconfirm';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import PackageAssignmentModal from '../../modals/PackageAssignmentModal';
import { IconButton } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { useIsMobile } from '../../hooks/useResponsive';
import { useLanguage } from '../../contexts/LanguageContext';
import { translate } from '../../locales';
import { TranslationKey } from '../../locales/types';
import html2pdf from 'html2pdf.js';
import './AdminClientsPage.css';

interface NullString {
  String: string;
  Valid: boolean;
}

interface AdminClient {
  id: string;
  email: NullString;
  country_code: NullString;
  phone: NullString;
  full_name: NullString;
  first_name: NullString;
  last_name: NullString;
  role: string;
  is_verified: boolean;
  phone_verified: boolean;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  business_name: NullString;
  business_type: NullString;
  business_address: NullString;
  business_phone: NullString;
  business_website: NullString;
  business_logo: NullString;
  business_description: NullString;
  facebook_url: NullString;
  instagram_url: NullString;
  twitter_url: NullString;
  linkedin_url: NullString;
  salon_activated: boolean;
  salon_id: NullString;
  package_name: string;
  package_active: boolean;
  package_expires_at: string | null;
}

const AdminClientsPage: React.FC = () => {
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const t = (key: TranslationKey) => translate(key, language as 'en' | 'ua');
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: string;
    userName: string;
  }>({
    open: false,
    userId: '',
    userName: '',
  });

  const [packageModal, setPackageModal] = useState<{
    open: boolean;
    userId: string;
    userName: string;
    userRole: string;
    currentPackage?: string;
    currentPackageExpiresAt?: string | null;
  }>({
    open: false,
    userId: '',
    userName: '',
    userRole: '',
    currentPackageExpiresAt: null,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${config.api.baseUrl}/admin/clients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to fetch clients');
      }

      const data = await response.json();
      console.log('Client data received:', data); // Debug log
      setClients(data && Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null, isMobile: boolean = false) => {
    if (!dateString) return 'Ніколи';

    if (isMobile) {
      // Shorter format for mobile: DD.MM.YY with time on new line
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('uk-UA', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      });
      const timeStr = date.toLocaleTimeString('uk-UA', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return (
        <div className="mobile-date">
          <div>{dateStr}</div>
          <div>{timeStr}</div>
        </div>
      );
    }

    // Full format for desktop
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };



  const handleRegeneratePasswordClick = (userId: string, userName: string) => {
    setConfirmDialog({
      open: true,
      userId,
      userName,
    });
  };

  const generatePasswordPDF = (userName: string, login: string, password: string) => {
    // Create HTML template with proper Ukrainian text and CSS
    const htmlContent = `
      <div style="
        font-family: 'Arial', 'Helvetica', sans-serif;
        max-width: 600px;
        margin: 0 auto;
        padding: 20px;
        background: white;
        color: #333;
      ">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 20px;">
          <h1 style="color: #2c3e50; margin: 0; font-size: 18px; font-weight: bold;">
            СИСТЕМА УПРАВЛІННЯ КЛІЄНТАМИ
          </h1>
        </div>

        <!-- Main Title -->
        <div style="text-align: center; margin-bottom: 10px;">
          <h2 style="color: #2c3e50; margin: 0; font-size: 16px; font-weight: bold;">
            НОВІ ДАНІ ДЛЯ ВХОДУ
          </h2>
        </div>

        <!-- Subtitle -->
        <div style="text-align: center; margin-bottom: 30px;">
          <p style="margin: 0; font-size: 12px; color: #666;">
            Інформація про обліковий запис клієнта
          </p>
        </div>

        <!-- User Info -->
        <div style="margin-bottom: 20px;">
          <p style="margin: 5px 0; font-size: 11px;">
            <strong>ПІБ клієнта:</strong> ${userName}
          </p>
          <p style="margin: 5px 0; font-size: 11px;">
            <strong>Дата генерації:</strong> ${new Date().toLocaleDateString('uk-UA')} ${new Date().toLocaleTimeString('uk-UA')}
          </p>
        </div>

        <!-- Credentials Table -->
        <table style="
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
          font-size: 11px;
        ">
          <thead>
            <tr>
              <th style="
                background-color: #3498db;
                color: white;
                padding: 8px;
                text-align: center;
                border: 1px solid #ddd;
                font-weight: bold;
              ">
                Дані облікового запису
              </th>
              <th style="
                background-color: #3498db;
                color: white;
                padding: 8px;
                text-align: center;
                border: 1px solid #ddd;
                font-weight: bold;
              ">
                Значення
              </th>
            </tr>
          </thead>
          <tbody>
            <tr style="background-color: #f9f9f9;">
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">
                Повне ім'я (ПІБ)
              </td>
              <td style="padding: 8px; border: 1px solid #ddd;">
                ${userName}
              </td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">
                Логін для входу
              </td>
              <td style="padding: 8px; border: 1px solid #ddd;">
                ${login}
              </td>
            </tr>
            <tr style="background-color: #f9f9f9;">
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">
                Новий пароль
              </td>
              <td style="padding: 8px; border: 1px solid #ddd; font-family: monospace; font-weight: bold; color: #e74c3c;">
                ${password}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Security Notice -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #e74c3c; margin: 0 0 10px 0; font-size: 12px;">
            УВАГА!
          </h3>
          <ul style="margin: 0; padding-left: 20px; font-size: 10px; line-height: 1.4;">
            <li>Зберігайте цей документ у безпечному місці</li>
            <li>Не передавайте пароль третім особам</li>
            <li>При першому вході рекомендується змінити пароль</li>
          </ul>
        </div>

        <!-- Footer -->
        <div style="text-align: center; border-top: 1px solid #ddd; padding-top: 15px;">
          <p style="margin: 0; font-size: 8px; color: #666;">
            Документ згенеровано автоматично системою управління клієнтами
          </p>
          <p style="margin: 3px 0 0 0; font-size: 8px; color: #666;">
            Сторінка 1 з 1 • ${new Date().toLocaleString('uk-UA')}
          </p>
        </div>
      </div>
    `;

    // Configure html2pdf options
    const options = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename: `дані_для_входу_клієнта_${userName.replace(/\s+/g, '_')}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: {
        unit: 'mm' as const,
        format: 'a4' as const,
        orientation: 'portrait' as const
      }
    };

    // Generate and download PDF
    html2pdf().set(options).from(htmlContent).save();
  };

  const handleConfirmRegeneratePassword = async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/admin/regenerate-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
        body: JSON.stringify({ user_id: confirmDialog.userId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to regenerate password');
      }

      const data = await response.json();

      // Generate PDF with user credentials
      generatePasswordPDF(
        confirmDialog.userName,
        data.login, // Use login from response
        data.new_password
      );

      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: `Пароль успішно перегенеровано. PDF файл завантажено.`,
        severity: 'success',
      }]);
    } catch (err) {
      console.error('Error regenerating password:', err);
      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: err instanceof Error ? err.message : 'Помилка при перегенерації пароля',
        severity: 'error',
      }]);
    } finally {
      setConfirmDialog({ open: false, userId: '', userName: '' });
    }
  };

  const handleCancelRegeneratePassword = () => {
    setConfirmDialog({ open: false, userId: '', userName: '' });
  };

  const handleCloseNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handlePackageClick = (client: AdminClient) => {
    const userName = client.full_name.Valid
      ? client.full_name.String
      : (client.first_name.Valid && client.last_name.Valid)
      ? `${client.last_name.String} ${client.first_name.String}`
      : 'Невідомий клієнт';

    setPackageModal({
      open: true,
      userId: client.id,
      userName,
      userRole: client.role,
      currentPackage: client.package_name || undefined,
      currentPackageExpiresAt: client.package_expires_at,
    });
  };

  const handlePackageModalClose = () => {
    setPackageModal({
      open: false,
      userId: '',
      userName: '',
      userRole: '',
      currentPackageExpiresAt: null,
    });
  };

  const handlePackageAssigned = () => {
    // Refresh the clients list to show updated package information
    fetchClients();
    const notificationId = Date.now().toString();
    setNotifications(prev => [...prev, {
      id: notificationId,
      message: 'Пакет успішно призначено',
      severity: 'success',
    }]);
  };

  if (loading) {
    return (
      <div className="admin-clients-page">
        <div className="loading-container">
          <LoadingSpinner size="large" />
          <p>{t('adminClients.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-clients-page">
        <div className="error-container">
          <h2>{t('adminClients.error')}</h2>
          <p>{error}</p>
          <button onClick={fetchClients} className="retry-button">
            {t('adminClients.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-clients-page">
      <div className="page-header">
        <h1>{t('adminClients.title')}</h1>
        <p>{t('adminClients.totalClients')} {clients.length}</p>
      </div>

      <div className="clients-table-container">
        <table className="clients-table">
          <thead>
            <tr>
              <th>{t('adminClients.tableHeaders.client')}</th>
              <th>{t('adminClients.tableHeaders.contacts')}</th>
              <th>{t('adminClients.tableHeaders.package')}</th>
              <th>{t('adminClients.tableHeaders.statistics')}</th>
              <th>{t('adminClients.tableHeaders.verification')}</th>
              <th>{t('adminClients.tableHeaders.lastLogin')}</th>
              <th>{t('adminClients.tableHeaders.created')}</th>
              <th>{t('adminClients.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td>
                  <div className="client-info-cell">
                    <div className="client-name">
                      {client.full_name.Valid
                        ? client.full_name.String
                        : (client.first_name.Valid && client.last_name.Valid)
                        ? `${client.last_name.String} ${client.first_name.String}`
                        : 'Невідомий клієнт'
                      }
                    </div>
                    <div className="client-details">
                      <div className="client-role">Роль: {client.role}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="contact-info">
                    {client.email.Valid && (
                      <div className="contact-item">
                        <EmailIcon sx={{ color: '#007bff', fontSize: 16, marginRight: 0.5 }} />
                        <span className="client-email">{client.email.String}</span>
                      </div>
                    )}
                    <div className="contact-item">
                      <PhoneIcon sx={{ color: '#007bff', fontSize: 16, marginRight: 0.5 }} />
                      {client.phone.Valid ? (
                        <a
                          href={`tel:${client.country_code.Valid ? client.country_code.String : ''}${client.phone.String}`}
                          className="client-phone-link"
                        >
                          {client.country_code.Valid ? `+${client.country_code.String} ` : ''}
                          {client.phone.String}
                        </a>
                      ) : (
                        <span className="client-phone-missing">відсутній</span>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div
                    className="package-info clickable"
                    onClick={() => handlePackageClick(client)}
                    title="Натисніть для призначення пакета"
                  >
                    <div className="package-name">
                      {client.package_name === 'Free' && client.package_active ? (
                        <>
                          {client.package_name} <span style={{
                            backgroundColor: '#d4edda',
                            color: '#155724',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            padding: '2px 6px',
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}>активний</span>
                        </>
                      ) : (client.package_name || 'Без пакета')}
                    </div>
                    {client.package_active && client.package_name !== 'Free' && (
                      <div className="package-status active">Активний</div>
                    )}

                    {client.package_active && client.package_expires_at && (
                      <div className="package-expiry">
                        Дійсний до: {new Date(client.package_expires_at).toLocaleDateString('uk-UA', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </div>
                    )}
                    {client.package_active && !client.package_expires_at && (
                      <div className="package-expiry">
                        Без терміну дії
                      </div>
                    )}
                    {!client.package_active && (
                      <div className="package-status inactive">Неактивний</div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="stats-info">
                    <div className="no-stats">Статистика недоступна</div>
                  </div>
                </td>
                <td>
                  <div className="verification-info">
                    <span className={`verification-badge ${client.is_verified ? 'verified' : 'unverified'}`}>
                      {client.is_verified ? 'Верифікований' : 'Не верифікований'}
                    </span>
                    {client.phone_verified && (
                      <span className="phone-verified">Телефон підтверджено</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="last-login-info">
                    {formatDate(client.last_login_at, isMobile)}
                  </div>
                </td>
                <td>
                  <div className="date-info">
                    {formatDate(client.created_at, isMobile)}
                  </div>
                </td>
                <td>
                  <div className="actions-cell">
                    <Tooltip content="Змінити пароль" position="top" maxWidth={150}>
                      <IconButton
                        onClick={() => handleRegeneratePasswordClick(
                          client.id,
                          client.full_name.Valid
                            ? client.full_name.String
                            : (client.first_name.Valid && client.last_name.Valid)
                            ? `${client.last_name.String} ${client.first_name.String}`
                            : 'Невідомий клієнт'
                        )}
                        size="small"
                        color="primary"
                      >
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {clients.length === 0 && (
        <div className="no-clients">
          <p>{t('adminClients.noClients')}</p>
        </div>
      )}

      {notifications.length > 0 && (
        <NotificationSnackbar
          notifications={notifications}
          onClose={handleCloseNotification}
        />
      )}

      <Popconfirm
        open={confirmDialog.open}
        onClose={handleCancelRegeneratePassword}
        onConfirm={handleConfirmRegeneratePassword}
        title={`Перегенерація пароля`}
        description={`Ви впевнені, що хочете перегенерувати пароль для клієнта ${confirmDialog.userName}?`}
        okText="Перегенерувати"
        cancelText="Скасувати"
        okButtonVariant="danger"
      />

      <PackageAssignmentModal
        isOpen={packageModal.open}
        onClose={handlePackageModalClose}
        onSuccess={handlePackageAssigned}
        userId={packageModal.userId}
        userName={packageModal.userName}
        currentPackage={packageModal.currentPackage}
        currentPackageExpiresAt={packageModal.currentPackageExpiresAt}
        userRole={packageModal.userRole}
      />
    </div>
  );
};

export default AdminClientsPage;
